import React from "react";
import {
  RotateCcw,
  Palette,
  Sparkles,
  MessageSquare,
  Video,
  Copy,
  Camera,
  Maximize2,
  Box,
  Boxes,
  Layers,
  PenTool,
  Compass,
  Share2,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";

export const RecreateMenu = ({ asset, className = "" }) => {
  const navigate = useNavigate();
  const [useInOpen, setUseInOpen] = React.useState(false);
  if (!asset || asset.type !== "image") return null;

  const handleUseInVideo = (e) => {
    e?.stopPropagation?.();
    const query = new URLSearchParams();
    query.set("asset", asset.id);
    query.set("mode", "image_to_video");
    if (asset.projectId) {
      query.set("project", asset.projectId);
    }
    navigate(`/tools/video?${query.toString()}`);
  };

  return (
    <DropdownMenu onOpenChange={(open) => { if (!open) setUseInOpen(false); }}>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid={`action-recreate-${asset.id}`}
              aria-label="Recrear y derivar"
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className={`relative flex h-7 w-7 items-center justify-center rounded-md border border-transparent text-xs text-[#a3a3a3] transition-all hover:border-[#f5b544]/40 hover:bg-[#f5b544]/15 hover:text-[#f5b544] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#f5b544] active:scale-95 data-[state=open]:border-[#f5b544]/40 data-[state=open]:bg-[#f5b544]/15 data-[state=open]:text-[#f5b544] ${className}`}
            >
              <RotateCcw size={14} />
            </button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          sideOffset={6}
          className="border border-[#303030] bg-[#141414] px-2 py-1 text-[11px] font-medium text-[#ededed] shadow-lg"
        >
          <span>Recrear</span>
        </TooltipContent>
      </Tooltip>

      <DropdownMenuContent
        align="center"
        side="top"
        sideOffset={8}
        className="w-56 border border-[#303030] bg-[#141414]/95 p-1.5 text-[#f4f1ec] shadow-2xl backdrop-blur-md"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {/* 1. Recreate */}
        <DropdownMenuItem
          disabled
          data-testid={`recreate-item-recreate-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <RotateCcw size={14} />
            <span>Recreate</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="my-1 border-t border-[#262626]" />

        {/* 2. Use as style */}
        <DropdownMenuItem
          disabled
          data-testid={`recreate-item-style-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <Palette size={14} />
            <span>Use as style</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        {/* 3. Use as reference */}
        <DropdownMenuItem
          disabled
          data-testid={`recreate-item-reference-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <Sparkles size={14} />
            <span>Use as reference</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        {/* 4. Recreate in chat */}
        <DropdownMenuItem
          disabled
          data-testid={`recreate-item-chat-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <MessageSquare size={14} />
            <span>Recreate in chat</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator className="my-1 border-t border-[#262626]" />

        {/* 5. Use in → (Submenu) */}
        <DropdownMenuSub open={useInOpen} onOpenChange={setUseInOpen}>
          <DropdownMenuSubTrigger
            data-testid={`recreate-subtrigger-use-in-${asset.id}`}
            onClick={(e) => {
              e.stopPropagation();
              setUseInOpen(true);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " " || e.key === "ArrowRight") {
                e.preventDefault();
                e.stopPropagation();
                setUseInOpen(true);
              }
            }}
            className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#d8d4cd] transition-colors outline-none cursor-pointer hover:bg-[#f5b544]/15 hover:text-[#f5b544] focus:bg-[#f5b544]/15 focus:text-[#f5b544] data-[state=open]:bg-[#f5b544]/15 data-[state=open]:text-[#f5b544]"
          >
            <div className="flex items-center gap-2.5">
              <Sparkles size={14} />
              <span>Use in</span>
            </div>
          </DropdownMenuSubTrigger>

          <DropdownMenuSubContent
            sideOffset={6}
            className="z-50 w-52 border border-[#303030] bg-[#141414]/95 p-1.5 text-[#f4f1ec] shadow-2xl backdrop-blur-md"
            onClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
          >
            {/* Video */}
            <DropdownMenuItem
              data-testid={`use-in-video-${asset.id}`}
              onSelect={handleUseInVideo}
              className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#d8d4cd] transition-colors outline-none cursor-pointer hover:bg-[#f5b544]/15 hover:text-[#f5b544] focus:bg-[#f5b544]/15 focus:text-[#f5b544]"
            >
              <Video size={14} />
              <span>Video</span>
            </DropdownMenuItem>

            {/* Variations */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-variations-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Copy size={14} />
                <span>Variations</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* Change Camera */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-change-camera-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Camera size={14} />
                <span>Change Camera</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* Upscale */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-upscale-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Maximize2 size={14} />
                <span>Upscale</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* Skin Enhancer */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-skin-enhancer-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Sparkles size={14} />
                <span>Skin Enhancer</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* 3D model */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-3d-model-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Box size={14} />
                <span>3D model</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* Create 3D scene */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-3d-scene-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Boxes size={14} />
                <span>Create 3D scene</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* Auto layers */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-auto-layers-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Layers size={14} />
                <span>Auto layers</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* Open in Designer */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-designer-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <PenTool size={14} />
                <span>Open in Designer</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>

            {/* Open in Spaces */}
            <DropdownMenuItem
              disabled
              data-testid={`use-in-spaces-${asset.id}`}
              className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
            >
              <div className="flex items-center gap-2.5">
                <Compass size={14} />
                <span>Open in Spaces</span>
              </div>
              <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
            </DropdownMenuItem>
          </DropdownMenuSubContent>
        </DropdownMenuSub>

        <DropdownMenuSeparator className="my-1 border-t border-[#262626]" />

        {/* 6. Export & publish */}
        <DropdownMenuItem
          disabled
          data-testid={`recreate-item-export-${asset.id}`}
          className="flex items-center justify-between gap-2.5 rounded-md px-2.5 py-1.5 text-xs text-[#6b6b6b] opacity-60 cursor-not-allowed outline-none select-none"
        >
          <div className="flex items-center gap-2.5">
            <Share2 size={14} />
            <span>Export & publish</span>
          </div>
          <span className="text-[10px] text-[#6b6b6b] font-medium">Próximamente</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default RecreateMenu;
